import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Tooltip,
  useTheme
} from '@material-ui/core';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  PhotoCamera as PhotoCameraIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  WbSunny as WbSunnyIcon,
  Opacity as OpacityIcon,
  Build as BuildIcon,
  Close as CloseIcon,
  BrokenImage as BrokenImageIcon,
  Speed as SpeedIcon,
  Print as PrintIcon
} from '@material-ui/icons';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 1400,
    margin: '0 auto'
  },
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  headerInfo: {
    flex: 1,
    minWidth: 200
  },
  badge: {
    marginLeft: theme.spacing(1),
    textTransform: 'capitalize'
  },
  mapCard: {
    marginBottom: theme.spacing(2),
    overflow: 'hidden'
  },
  mapContainer: {
    height: 350,
    width: '100%',
    position: 'relative',
    zIndex: 1
  },
  summaryCard: {
    marginBottom: theme.spacing(2)
  },
  summaryItem: {
    textAlign: 'center',
    padding: theme.spacing(1.5)
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4
  },
  summaryValue: {
    fontWeight: 600,
    fontSize: '1rem'
  },
  ratingChip: {
    fontWeight: 600
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600
  },
  accordion: {
    marginBottom: theme.spacing(1),
    '&:before': { display: 'none' },
    borderRadius: '8px !important',
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden'
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(1)
    }
  },
  zoneChip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5)
  },
  checkItem: {
    padding: theme.spacing(0.5, 0)
  },
  issueItem: {
    borderLeft: `3px solid ${theme.palette.warning.main}`,
    paddingLeft: theme.spacing(1.5),
    marginBottom: theme.spacing(1)
  },
  criticalIssue: {
    borderLeftColor: theme.palette.error.main
  },
  repairItem: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 4,
    backgroundColor: theme.palette.background.default
  },
  controllerCard: {
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: theme.spacing(1)
  },
  photoThumb: {
    width: '100%',
    height: 140,
    objectFit: 'cover',
    borderRadius: 4,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    '&:hover': { opacity: 0.85 }
  },
  photoTag: {
    fontSize: '0.65rem',
    height: 18,
    marginTop: 2
  },
  lightboxImg: {
    maxWidth: '90vw',
    maxHeight: '80vh',
    objectFit: 'contain'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  zoneMiniMap: {
    height: 180,
    width: '100%',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    position: 'relative',
    zIndex: 1,
    overflow: 'hidden'
  },
  zoneContentRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column'
    }
  },
  zoneMapCol: {
    flex: '0 0 45%',
    maxWidth: '45%',
    overflow: 'hidden',
    borderRadius: 6,
    [theme.breakpoints.down('xs')]: {
      flex: '0 0 100%',
      maxWidth: '100%'
    }
  },
  zoneInfoCol: {
    flex: 1,
    minWidth: 0
  }
}));

const RATING_COLORS = {
  'Excellent': '#4caf50',
  'Good': '#8bc34a',
  'Fair': '#ff9800',
  'Poor': '#f44336',
  'Critical': '#b71c1c'
};

const PRIORITY_COLORS = {
  urgent: '#b71c1c',
  high: '#f44336',
  medium: '#ff9800',
  low: '#4caf50'
};

const ENTITY_TYPE_LABELS = {
  controller: 'Controller',
  valveBox: 'Valve Box',
  valve: 'Valve',
  zone: 'Zone',
  site: 'Site',
  issue: 'Issue',
  quickInspection: 'Quick Inspection'
};

const SEVERITY_COLORS = {
  critical: '#b71c1c',
  major: '#f44336',
  moderate: '#ff9800',
  minor: '#ffc107'
};

const CHECKLIST_LABELS = {
  systemInspected: 'System Inspected',
  controllersDocumented: 'Controllers Documented',
  valveBoxesDocumented: 'Valve Boxes Documented',
  zonesDocumented: 'Zones Documented',
  backflowChecked: 'Backflow Checked',
  rainSensorChecked: 'Rain Sensor Checked',
  accessInfoDocumented: 'Access Info Documented',
  allZonesManuallyRun: 'All Zones Manually Run',
  allIsolationValvesExercised: 'Isolation Valves Exercised',
  allEmittersChecked: 'All Emitters Checked',
  allFiltersCleaned: 'All Filters Cleaned',
  allSprayPatternsAdjusted: 'Spray Patterns Adjusted',
  allWaterLinesInspected: 'Water Lines Inspected',
  allValveBoxesInspected: 'Valve Boxes Inspected'
};

function InspectionDetailPage(props) {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const siteId = searchParams.get('siteId') || '';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [propertyLines, setPropertyLines] = useState([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const zoneMiniMapsRef = useRef({});
  const propertyLinesAddedRef = useRef(false);

  useEffect(() => {
    fetchInspectionDetails();
  }, [id, siteId]);

  const fetchInspectionDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${props.url}/api/hmi/genericGetRequest?type=IRRIGATION_INSPECTION_DETAILS&inspectionId=${id}&siteId=${siteId}`
      );
      if (response.data?.success && response.data.data) {
        setData(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load inspection details');
      }
    } catch (err) {
      console.error('Error fetching inspection details:', err);
      setError('Failed to load inspection details');
    }
    setLoading(false);
  };

  // Fetch property lines when we have data
  const fetchPropertyLines = async (jobSiteId) => {
    try {
      const response = await axios.get(`${props.url}/api/customer/getAPNAndParcelInfo?id=${jobSiteId}`);
      let fetchedLines = [];
      const results = response?.data?.length ? response.data : (response?.data?.results?.length ? response.data.results : []);
      if (results.length) {
        fetchedLines = results
          .filter(property => property?.googleMapsFormat?.length > 0)
          .map((property, index) => ({
            APN: property.properties?.APN,
            address: property.properties?.address,
            paths: property.googleMapsFormat.map(p => ({ lat: p.lat, lng: p.lng }))
          }));
      }
      setPropertyLines(fetchedLines);
    } catch (e) {
      console.log('Error fetching property lines:', e);
      setPropertyLines([]);
    }
  };

  const handlePrintReport = async () => {
    setGeneratingPdf(true);
    try {
      const response = await axios.get(
        `${props.url}/api/hmi/genericGetRequest?type=IRRIGATION_INSPECTION_PDF&inspectionId=${id}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate inspection report');
    }
    setGeneratingPdf(false);
  };

  useEffect(() => {
    if (data?.jobSite?._id) {
      fetchPropertyLines(data.jobSite._id);
    }
  }, [data]);

  // When property lines arrive after map is already rendered, add them
  useEffect(() => {
    if (mapRef.current && propertyLines.length > 0 && !propertyLinesAddedRef.current) {
      propertyLinesAddedRef.current = true;
      const bounds = L.latLngBounds();
      propertyLines.forEach((line) => {
        if (!line.paths || line.paths.length === 0) return;
        const layer = L.polygon(line.paths, {
          color: '#ffd600',
          weight: 2,
          opacity: 0.9,
          fillColor: '#ffd600',
          fillOpacity: 0.05,
          interactive: false
        });
        if (line.APN) layer.bindTooltip(line.APN);
        layer.addTo(mapRef.current);
        bounds.extend(layer.getBounds());
      });
      // Re-fit map to include property lines
      // Gather existing marker bounds too
      mapRef.current.eachLayer((layer) => {
        if (layer.getLatLng) bounds.extend(layer.getLatLng());
      });
      if (bounds.isValid()) {
        mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 19 });
      }
    }
  }, [propertyLines]);

  // Initialize Leaflet map via callback ref — fires when the div actually mounts in the DOM
  const initMap = useCallback((node) => {
    mapContainerRef.current = node;
    if (!node || !data || mapRef.current) return;

    const jobSite = data.jobSite;
    // JobSite may have top-level latitude/longitude OR location.coordinates [lng, lat]
    const coords = jobSite?.location?.coordinates;
    const lat = jobSite?.latitude || (coords && coords[1]) || 33.45;
    const lng = jobSite?.longitude || (coords && coords[0]) || -112.07;

    const map = L.map(node, {
      center: [lat, lng],
      zoom: 17,
      zoomControl: true
    });

    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles &copy; Esri',
      maxZoom: 20
    }).addTo(map);

    // Jobsite marker
    const siteIcon = L.divIcon({
      html: '<div style="background:#1976d2;color:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:16px;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);">&#9873;</div>',
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      className: ''
    });
    L.marker([lat, lng], { icon: siteIcon })
      .addTo(map)
      .bindPopup(`<b>${jobSite?.name || 'Job Site'}</b><br/>${jobSite?.address || ''}`);

    const bounds = L.latLngBounds([[lat, lng]]);

    // Zone water points — from systemConfigs.zones (the real Zone model docs with waterPoints)
    const zoneColors = ['#4caf50', '#2196f3', '#ff9800', '#e91e63', '#9c27b0', '#00bcd4', '#ff5722', '#8bc34a'];
    const sysZones = data.systemConfigs?.zones || [];
    sysZones.forEach((zone, zIdx) => {
      const zoneColor = zoneColors[zIdx % zoneColors.length];
      if (zone.waterPoints?.length) {
        zone.waterPoints.forEach((wp) => {
          if (wp.latitude && wp.longitude) {
            const wpIcon = L.divIcon({
              html: `<div style="background:${zoneColor};border-radius:50%;width:12px;height:12px;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);"></div>`,
              iconSize: [12, 12],
              iconAnchor: [6, 6],
              className: ''
            });
            L.marker([wp.latitude, wp.longitude], { icon: wpIcon })
              .addTo(map)
              .bindPopup(`<b>${zone.name || `Zone ${zone.number || zIdx + 1}`}</b><br/>${zone.type || ''} — Water Point`);
            bounds.extend([wp.latitude, wp.longitude]);
          }
        });
      }
    });

    // Also check inspection.zoneDetails for waterPoints (in case systemConfigs zones are empty)
    const inspection = data.inspection;
    if (inspection?.zoneDetails?.length && sysZones.length === 0) {
      inspection.zoneDetails.forEach((zone, zIdx) => {
        if (zone.waterPoints?.length) {
          zone.waterPoints.forEach((wp) => {
            if (wp.latitude && wp.longitude) {
              const wpIcon = L.divIcon({
                html: `<div style="background:#4caf50;border-radius:50%;width:12px;height:12px;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);"></div>`,
                iconSize: [12, 12],
                iconAnchor: [6, 6],
                className: ''
              });
              L.marker([wp.latitude, wp.longitude], { icon: wpIcon })
                .addTo(map)
                .bindPopup(`<b>${zone.name || `Zone ${zone.number || zIdx + 1}`}</b><br/>Water Point`);
              bounds.extend([wp.latitude, wp.longitude]);
            }
          });
        }
      });
    }

    // Controller markers from systemConfigs — location is GeoJSON { coordinates: [lng, lat] }
    const sysControllers = data.systemConfigs?.controllers || [];
    sysControllers.forEach((ctrl) => {
      const ctrlCoords = ctrl.location?.coordinates;
      const ctrlLat = ctrlCoords && ctrlCoords[1] && ctrlCoords[0] ? ctrlCoords[1] : null;
      const ctrlLng = ctrlCoords && ctrlCoords[1] && ctrlCoords[0] ? ctrlCoords[0] : null;
      if (ctrlLat && ctrlLng && (ctrlLat !== 0 || ctrlLng !== 0)) {
        const ctrlIcon = L.divIcon({
          html: '<div style="background:#9c27b0;color:white;border-radius:4px;width:22px;height:22px;display:flex;align-items:center;justify-content:center;font-size:12px;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);">C</div>',
          iconSize: [22, 22],
          iconAnchor: [11, 11],
          className: ''
        });
        L.marker([ctrlLat, ctrlLng], { icon: ctrlIcon })
          .addTo(map)
          .bindPopup(`<b>${ctrl.brand || ''} ${ctrl.model || ''}</b><br/>${ctrl.numZones || 0} zones`);
        bounds.extend([ctrlLat, ctrlLng]);
      }
    });

    // Valve box markers — location is GeoJSON { coordinates: [lng, lat] }
    const vBoxes = data.valveBoxes || [];
    vBoxes.forEach((vb, vIdx) => {
      const vbCoords = vb.location?.coordinates;
      const vbLat = vbCoords && vbCoords[1] && vbCoords[0] ? vbCoords[1] : null;
      const vbLng = vbCoords && vbCoords[1] && vbCoords[0] ? vbCoords[0] : null;
      if (vbLat && vbLng && (vbLat !== 0 || vbLng !== 0)) {
        const vbIcon = L.divIcon({
          html: '<div style="background:#ff9800;color:white;border-radius:4px;width:20px;height:20px;display:flex;align-items:center;justify-content:center;font-size:11px;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);">V</div>',
          iconSize: [20, 20],
          iconAnchor: [10, 10],
          className: ''
        });
        L.marker([vbLat, vbLng], { icon: vbIcon })
          .addTo(map)
          .bindPopup(`<b>${vb.name || `Valve Box ${vIdx + 1}`}</b><br/>${vb.condition || ''}`);
        bounds.extend([vbLat, vbLng]);
      }
    });

    // Photo location markers
    if (data.photos?.length) {
      data.photos.forEach((photo) => {
        if (photo.location?.coordinates?.length === 2) {
          const [pLng, pLat] = photo.location.coordinates;
          if (pLat && pLng) {
            const camIcon = L.divIcon({
              html: '<div style="background:#ff5722;color:white;border-radius:50%;width:16px;height:16px;display:flex;align-items:center;justify-content:center;font-size:10px;border:1px solid white;">&#128247;</div>',
              iconSize: [16, 16],
              iconAnchor: [8, 8],
              className: ''
            });
            L.marker([pLat, pLng], { icon: camIcon })
              .addTo(map)
              .bindPopup(`<b>Photo</b><br/>${ENTITY_TYPE_LABELS[photo.entityType] || photo.entityType}`);
            bounds.extend([pLat, pLng]);
          }
        }
      });
    }

    // Property line polygons
    if (propertyLines.length > 0) {
      propertyLinesAddedRef.current = true;
      propertyLines.forEach((line) => {
        if (!line.paths || line.paths.length === 0) return;
        const layer = L.polygon(line.paths, {
          color: '#ffd600',
          weight: 2,
          opacity: 0.9,
          fillColor: '#ffd600',
          fillOpacity: 0.05,
          interactive: false
        });
        if (line.APN) layer.bindTooltip(line.APN);
        layer.addTo(map);
        bounds.extend(layer.getBounds());
      });
    }

    // Fit bounds — property lines + all markers
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 19 });
    }

    mapRef.current = map;

    // Leaflet needs a size recalculation after container is fully rendered
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 200);
  }, [data, propertyLines]);

  // Clean up maps on unmount
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      Object.values(zoneMiniMapsRef.current).forEach(m => { try { m.remove(); } catch(e) {} });
      zoneMiniMapsRef.current = {};
    };
  }, []);

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box className={classes.root}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/hmiirrigationdashboard')}>
          Back to Dashboard
        </Button>
        <Box className={classes.emptyState}>
          <Typography variant="h6">{error || 'No data found'}</Typography>
        </Box>
      </Box>
    );
  }

  const inspection = data.inspection;
  const jobSite = data.jobSite;
  const systemConfigs = data.systemConfigs;
  const photos = data.photos || [];
  const findings = inspection?.findings || {};
  const zoneDetails = inspection?.zoneDetails || [];
  const repairsNeeded = findings.repairsNeeded || [];
  const recommendations = findings.recommendations || [];

  // Discovered assets from systemConfigs (the real DB docs with waterPoints, photos, etc.)
  const sysZones = systemConfigs?.zones || [];
  const sysControllers = systemConfigs?.controllers || [];
  // Build lookup by string id for cross-referencing with inspection.discoveredAssets
  const zoneById = {};
  sysZones.forEach(z => { if (z.id) zoneById[z.id] = z; if (z._id) zoneById[z._id.toString()] = z; });
  const controllerById = {};
  sysControllers.forEach(c => { if (c.id) controllerById[c.id] = c; if (c._id) controllerById[c._id.toString()] = c; });

  // Merge inspection.zoneDetails with systemConfigs zones for richer data
  // The inspection.discoveredAssets.zones contains IDs of the Zone model docs
  const discoveredZoneIds = inspection?.discoveredAssets?.zones || [];
  const discoveredControllerIds = inspection?.discoveredAssets?.controllers || [];
  const discoveredValveBoxIds = inspection?.discoveredAssets?.valveBoxes || [];

  // Use real DB docs; fallback to findings snapshots
  const controllers = sysControllers.length > 0 ? sysControllers : (findings.controllers || []);
  const valveBoxes = (data.valveBoxes && data.valveBoxes.length > 0) ? data.valveBoxes : (findings.valveBoxes || []);
  const valves = data.valves || [];

  // Build valve/valveBox lookups for zone→valve→valveBox mapping
  const valveById = {};
  valves.forEach(v => { if (v._id) valveById[v._id.toString()] = v; });
  const valveBoxById = {};
  valveBoxes.forEach(vb => { if (vb._id) valveBoxById[vb._id.toString()] = vb; });

  const duration = inspection?.startTime && inspection?.endTime
    ? Math.round((new Date(inspection.endTime) - new Date(inspection.startTime)) / 60000)
    : null;

  // Group photos by entityType
  const photosByType = {};
  photos.forEach((p) => {
    const type = p.entityType || 'other';
    if (!photosByType[type]) photosByType[type] = [];
    photosByType[type].push(p);
  });

  // Build photo lookup by entityId for matching to zones/controllers/valveboxes
  // Key by both entityId and entityServerId (toString) for robust matching
  const photosByEntityId = {};
  photos.forEach((p) => {
    if (p.entityId) {
      if (!photosByEntityId[p.entityId]) photosByEntityId[p.entityId] = [];
      photosByEntityId[p.entityId].push(p);
    }
    if (p.entityServerId) {
      const servId = p.entityServerId.toString ? p.entityServerId.toString() : p.entityServerId;
      if (!photosByEntityId[servId]) photosByEntityId[servId] = [];
      photosByEntityId[servId].push(p);
    }
  });

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.headerBar}>
        <IconButton onClick={() => history.push('/hmiirrigationdashboard')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <div className={classes.headerInfo}>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            {jobSite?.name || 'Inspection Details'}
            <Chip
              label={inspection?.inspectionType || 'inspection'}
              size="small"
              className={classes.badge}
              style={{ backgroundColor: theme.palette.info.light, color: '#fff' }}
            />
            {inspection?.systemRating && (
              <Chip
                label={inspection.systemRating}
                size="small"
                className={`${classes.badge} ${classes.ratingChip}`}
                style={{
                  backgroundColor: RATING_COLORS[inspection.systemRating] || '#999',
                  color: '#fff'
                }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {inspection?.startTime ? moment(inspection.startTime).format('MMMM D, YYYY [at] h:mm A') : ''}
            {inspection?.inspectorName ? ` — ${inspection.inspectorName}` : ''}
            {jobSite?.address ? ` | ${jobSite.address}` : ''}
          </Typography>
        </div>
        <Button
          variant="outlined"
          size="small"
          startIcon={generatingPdf ? <CircularProgress size={16} /> : <PrintIcon />}
          onClick={handlePrintReport}
          disabled={generatingPdf}
          style={{ whiteSpace: 'nowrap' }}
        >
          {generatingPdf ? 'Generating...' : 'Print Report'}
        </Button>
      </div>

      {/* Map */}
      <Paper className={classes.mapCard} elevation={2}>
        <div ref={initMap} className={classes.mapContainer} />
      </Paper>

      {/* Summary Card */}
      <Paper className={classes.summaryCard} elevation={1}>
        <CardHeader title="Inspection Summary" titleTypographyProps={{ variant: 'subtitle1', style: { fontWeight: 600 } }} />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2} className={classes.summaryItem}>
              <Typography className={classes.summaryLabel}>Type</Typography>
              <Typography className={classes.summaryValue} style={{ textTransform: 'capitalize' }}>
                {inspection?.inspectionType || '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2} className={classes.summaryItem}>
              <Typography className={classes.summaryLabel}>Duration</Typography>
              <Typography className={classes.summaryValue}>
                {duration ? `${duration} min` : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2} className={classes.summaryItem}>
              <Typography className={classes.summaryLabel}>Weather</Typography>
              <Typography className={classes.summaryValue}>
                {inspection?.weatherConditions?.temperature
                  ? `${inspection.weatherConditions.temperature}°F ${inspection.weatherConditions.conditions || ''}`
                  : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2} className={classes.summaryItem}>
              <Typography className={classes.summaryLabel}>Water Status</Typography>
              <Typography className={classes.summaryValue}>
                {inspection?.systemStatus?.waterOn !== undefined
                  ? (inspection.systemStatus.waterOn ? 'Water ON' : 'Water OFF')
                  : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2} className={classes.summaryItem}>
              <Typography className={classes.summaryLabel}>Pressure</Typography>
              <Typography className={classes.summaryValue}>
                {inspection?.systemStatus?.waterPressure?.static
                  ? `${inspection.systemStatus.waterPressure.static} ${inspection.systemStatus.waterPressure.unit || 'psi'}`
                  : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2} className={classes.summaryItem}>
              <Typography className={classes.summaryLabel}>Issues</Typography>
              <Typography className={classes.summaryValue} style={{ color: (findings.totalIssues || 0) > 0 ? theme.palette.warning.main : theme.palette.success.main }}>
                {findings.totalIssues || 0} total / {findings.criticalIssues || 0} critical
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>

      {/* Inspection Checklist — only show completed items */}
      {inspection?.checklistItems && (() => {
        const completedItems = Object.entries(inspection.checklistItems)
          .filter(([key, val]) => val && val.completed === true && CHECKLIST_LABELS[key]);
        return completedItems.length > 0 ? (
          <Paper style={{ padding: 12, marginBottom: 16 }} elevation={1}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              Inspection Checklist ({completedItems.length}/{Object.keys(inspection.checklistItems).filter(k => CHECKLIST_LABELS[k]).length} completed)
            </Typography>
            <Grid container spacing={1}>
              {completedItems.map(([key, val]) => (
                <Grid item xs={6} sm={4} md={3} key={key}>
                  <Box display="flex" alignItems="center" className={classes.checkItem}>
                    <CheckCircleIcon style={{ fontSize: 16, color: '#4caf50', marginRight: 6 }} />
                    <Typography variant="body2">{CHECKLIST_LABELS[key]}</Typography>
                  </Box>
                  {val.timestamp && (
                    <Typography variant="caption" color="textSecondary" style={{ paddingLeft: 22 }}>
                      {moment(val.timestamp).format('MMM D, h:mm A')}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          </Paper>
        ) : null;
      })()}

      <Grid container spacing={2}>
        {/* Discovered Zones — from systemConfigs with waterPoints + merged with inspection zoneDetails */}
        {sysZones.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Zones ({sysZones.length})
            </Typography>
            {sysZones.map((zone, idx) => {
              // Cross-reference with inspection.zoneDetails by id
              const inspZone = zoneDetails.find(z => z.id === zone.id) || {};
              // Photos from InspectionPhoto matched by entityId
              const zonePhotos = photosByEntityId[zone.id] || photosByEntityId[zone._id?.toString()] || [];
              // Also include photos stored directly on the zone model
              const modelPhotos = zone.photos || [];
              const zoneColor = ['#4caf50', '#2196f3', '#ff9800', '#e91e63', '#9c27b0', '#00bcd4', '#ff5722', '#8bc34a'][idx % 8];
              return (
              <Accordion key={zone._id || zone.id || idx} className={classes.accordion} defaultExpanded={idx === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                  <Avatar style={{ width: 30, height: 30, fontSize: 14, backgroundColor: zoneColor }}>
                    {zone.number || idx + 1}
                  </Avatar>
                  <Typography style={{ fontWeight: 500 }}>
                    {zone.name || `Zone ${zone.number || idx + 1}`}
                  </Typography>
                  {zone.type && <Chip label={zone.type} size="small" variant="outlined" className={classes.zoneChip} />}
                  {zone.maintenanceStatus && (
                    <Chip
                      label={zone.maintenanceStatus}
                      size="small"
                      style={{
                        backgroundColor: zone.maintenanceStatus === 'operational' ? '#e8f5e9' : zone.maintenanceStatus === 'needs_replacement' ? '#ffebee' : '#fff3e0',
                        textTransform: 'capitalize'
                      }}
                    />
                  )}
                  {zone.waterPoints?.length > 0 && (
                    <Chip label={`${zone.waterPoints.length} water pts`} size="small" variant="outlined" style={{ borderColor: zoneColor, color: zoneColor }} />
                  )}
                </AccordionSummary>
                <AccordionDetails style={{ display: 'block' }}>
                  {/* Side-by-side: mini-map (left) + zone info (right) */}
                  <div className={classes.zoneContentRow}>
                    {/* Mini-map — only this zone's water points */}
                    {zone.waterPoints?.length > 0 && (
                      <div className={classes.zoneMapCol}>
                        <div
                          className={classes.zoneMiniMap}
                          ref={(node) => {
                            const key = zone._id || zone.id || `zone-${idx}`;
                            if (!node || zoneMiniMapsRef.current[key]) return;
                            const wps = zone.waterPoints.filter(wp => wp.latitude && wp.longitude);
                            if (wps.length === 0) return;
                            const miniMap = L.map(node, {
                              zoomControl: false,
                              attributionControl: false,
                              dragging: true,
                              scrollWheelZoom: false
                            });
                            L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                              maxZoom: 21
                            }).addTo(miniMap);
                            const miniBounds = L.latLngBounds();
                            wps.forEach((wp) => {
                              const wpIcon = L.divIcon({
                                html: `<div style="background:${zoneColor};border-radius:50%;width:10px;height:10px;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
                                iconSize: [10, 10],
                                iconAnchor: [5, 5],
                                className: ''
                              });
                              L.marker([wp.latitude, wp.longitude], { icon: wpIcon }).addTo(miniMap);
                              miniBounds.extend([wp.latitude, wp.longitude]);
                            });
                            // Show the valve box associated with this zone (zone→valve→valveBox)
                            if (zone.valveId) {
                              const zoneValve = valveById[zone.valveId.toString ? zone.valveId.toString() : zone.valveId];
                              if (zoneValve?.valveBoxId) {
                                const zoneVb = valveBoxById[zoneValve.valveBoxId.toString ? zoneValve.valveBoxId.toString() : zoneValve.valveBoxId];
                                const vbC = zoneVb?.location?.coordinates;
                                if (vbC && vbC[1] && vbC[0]) {
                                  const vbI = L.divIcon({
                                    html: '<div style="background:#ff9800;border-radius:3px;width:10px;height:10px;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);font-size:7px;color:white;display:flex;align-items:center;justify-content:center;">V</div>',
                                    iconSize: [10, 10],
                                    iconAnchor: [5, 5],
                                    className: ''
                                  });
                                  L.marker([vbC[1], vbC[0]], { icon: vbI }).addTo(miniMap).bindPopup(zoneVb.name || 'Valve Box');
                                  miniBounds.extend([vbC[1], vbC[0]]);
                                }
                              }
                            }
                            // Property lines on zone mini-map
                            propertyLines.forEach((line) => {
                              if (!line.paths || line.paths.length === 0) return;
                              L.polygon(line.paths, {
                                color: '#ffd600',
                                weight: 1.5,
                                opacity: 0.8,
                                fillColor: '#ffd600',
                                fillOpacity: 0.03,
                                interactive: false
                              }).addTo(miniMap);
                            });
                            if (miniBounds.isValid()) {
                              miniMap.fitBounds(miniBounds, { padding: [15, 15], maxZoom: 19 });
                            }
                            zoneMiniMapsRef.current[key] = miniMap;
                            setTimeout(() => miniMap.invalidateSize(), 150);
                          }}
                        />
                      </div>
                    )}

                    {/* Zone info beside the map */}
                    <div className={classes.zoneInfoCol}>
                      <Grid container spacing={1}>
                        {zone.valveType && (
                          <Grid item xs={6}><Typography variant="body2">Valve Type: <strong>{zone.valveType}</strong></Typography></Grid>
                        )}
                        {zone.valveSize > 0 && (
                          <Grid item xs={6}><Typography variant="body2">Valve Size: <strong>{zone.valveSize}"</strong></Typography></Grid>
                        )}
                        {zone.waterNeeds && (
                          <Grid item xs={6}><Typography variant="body2">Water Needs: <strong>{zone.waterNeeds}</strong></Typography></Grid>
                        )}
                        {zone.runTime && (
                          <Grid item xs={6}><Typography variant="body2">Run Time: <strong>{zone.runTime}</strong></Typography></Grid>
                        )}
                        {zone.flowRate && (
                          <Grid item xs={6}><Typography variant="body2">Flow Rate: <strong>{zone.flowRate}</strong></Typography></Grid>
                        )}
                        {zone.operatingPressure && (
                          <Grid item xs={6}><Typography variant="body2">Pressure: <strong>{zone.operatingPressure}</strong></Typography></Grid>
                        )}
                        {zone.condition && (
                          <Grid item xs={6}><Typography variant="body2">Condition: <strong style={{ textTransform: 'capitalize' }}>{zone.condition}</strong></Typography></Grid>
                        )}
                        {zone.area && zone.area !== '0' && (
                          <Grid item xs={6}><Typography variant="body2">Area: <strong>{zone.area} {zone.areaUnit || 'sq ft'}</strong></Typography></Grid>
                        )}
                      </Grid>

                      {/* Emitter details */}
                      {zone.emitterDetails?.length > 0 && (
                        <Box mt={1}>
                          <Typography variant="subtitle2" gutterBottom>Emitters</Typography>
                          {zone.emitterDetails.map((em, eIdx) => (
                            <Typography key={eIdx} variant="body2">
                              {em.type}{em.brand ? ` — ${em.brand}` : ''}{em.quantity ? ` (${em.quantity})` : ''}{em.flowRate ? ` @ ${em.flowRate} ${em.flowRateUnit || 'GPH'}` : ''}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      {/* Zone notes */}
                      {(zone.notes || inspZone.notes || inspZone.details) && (
                        <Box mt={1}>
                          <Typography variant="body2" color="textSecondary">
                            {zone.notes || inspZone.notes || inspZone.details}
                          </Typography>
                        </Box>
                      )}
                    </div>
                  </div>

                  {/* Issues from inspection zoneDetails */}
                  {inspZone.issues?.length > 0 && (
                    <Box mb={1}>
                      <Typography variant="subtitle2" gutterBottom>Issues</Typography>
                      {inspZone.issues.map((issue, iIdx) => (
                        <Box key={issue.id || iIdx} className={`${classes.issueItem} ${issue.severity === 'critical' || issue.severity === 'major' ? classes.criticalIssue : ''}`} mb={0.5}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {issue.title || issue.type || 'Issue'}
                            {issue.severity && (
                              <Chip
                                label={issue.severity}
                                size="small"
                                style={{ marginLeft: 6, height: 18, fontSize: '0.65rem', backgroundColor: SEVERITY_COLORS[issue.severity] || '#999', color: '#fff' }}
                              />
                            )}
                          </Typography>
                          {issue.description && <Typography variant="caption" color="textSecondary">{issue.description}</Typography>}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Zone photos — from InspectionPhoto records + model photos */}
                  {(zonePhotos.length > 0 || modelPhotos.length > 0) && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Photos ({zonePhotos.length + modelPhotos.length})</Typography>
                      <div className={classes.photoGrid}>
                        {zonePhotos.map((photo, pIdx) => (
                          <img
                            key={`ip-${pIdx}`}
                            src={photo.photoUrl}
                            alt={`Zone photo`}
                            className={classes.photoThumb}
                            onClick={() => setLightboxPhoto({ url: photo.photoUrl, caption: `${zone.name || `Zone ${idx + 1}`}${photo.capturedAt ? ` — ${moment(photo.capturedAt).format('MMM D, h:mm A')}` : ''}` })}
                          />
                        ))}
                        {modelPhotos.map((photoUrl, pIdx) => (
                          <img
                            key={`mp-${pIdx}`}
                            src={photoUrl}
                            alt={`Zone photo`}
                            className={classes.photoThumb}
                            onClick={() => setLightboxPhoto({ url: photoUrl, caption: zone.name || `Zone ${idx + 1}` })}
                          />
                        ))}
                      </div>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
              );
            })}
          </Grid>
        )}

        {/* Inspection zoneDetails fallback (if no systemConfigs zones) */}
        {sysZones.length === 0 && zoneDetails.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Zone Details ({zoneDetails.length})
            </Typography>
            {zoneDetails.map((zone, idx) => (
              <Accordion key={zone.id || idx} className={classes.accordion} defaultExpanded={idx === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                  <Avatar style={{ width: 30, height: 30, fontSize: 14, backgroundColor: theme.palette.info.main }}>
                    {zone.number || idx + 1}
                  </Avatar>
                  <Typography style={{ fontWeight: 500 }}>
                    {zone.name || `Zone ${zone.number || idx + 1}`}
                  </Typography>
                  {zone.type && <Chip label={zone.type} size="small" variant="outlined" className={classes.zoneChip} />}
                </AccordionSummary>
                <AccordionDetails style={{ display: 'block' }}>
                  <Typography variant="body2" color="textSecondary">{zone.notes || zone.details || 'No details available'}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        )}

        {/* Controllers */}
        {controllers.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Controllers ({controllers.length})
            </Typography>
            {controllers.map((ctrl, cIdx) => {
              // Match InspectionPhotos by string id, MongoDB _id, or fall back to entityType match
              let ctrlPhotos = photosByEntityId[ctrl.id] || photosByEntityId[ctrl._id?.toString()] || [];
              // Fallback: if no photos matched by entityId, gather all 'controller' entityType photos
              // that aren't already claimed by another controller
              if (ctrlPhotos.length === 0 && controllers.length === 1 && photosByType['controller']?.length > 0) {
                ctrlPhotos = photosByType['controller'];
              }
              const modelPhotos = ctrl.photos || [];
              const allCtrlPhotos = [...(ctrl.photoUri ? [ctrl.photoUri] : []), ...modelPhotos].filter(Boolean);
              return (
              <Card key={ctrl._id || ctrl.id || cIdx} className={classes.controllerCard} variant="outlined">
                <CardContent style={{ paddingBottom: 12 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      {ctrl.brand || ''} {ctrl.model || `Controller ${cIdx + 1}`}
                    </Typography>
                    {ctrl.condition && (
                      <Chip
                        label={ctrl.condition}
                        size="small"
                        style={{
                          textTransform: 'capitalize',
                          backgroundColor: ctrl.condition === 'good' || ctrl.condition === 'excellent' ? '#e8f5e9' : ctrl.condition === 'poor' ? '#ffebee' : '#fff3e0'
                        }}
                      />
                    )}
                    {ctrl.maintenanceStatus && (
                      <Chip
                        label={ctrl.maintenanceStatus.replace('_', ' ')}
                        size="small"
                        variant="outlined"
                        style={{ textTransform: 'capitalize', marginLeft: 4 }}
                      />
                    )}
                  </Box>
                  <Grid container spacing={1}>
                    {ctrl.brand && (
                      <Grid item xs={6}><Typography variant="body2">Brand: <strong>{ctrl.brand}</strong></Typography></Grid>
                    )}
                    {ctrl.model && (
                      <Grid item xs={6}><Typography variant="body2">Model: <strong>{ctrl.model}</strong></Typography></Grid>
                    )}
                    {ctrl.serialNumber && (
                      <Grid item xs={6}><Typography variant="body2">Serial: <strong>{ctrl.serialNumber}</strong></Typography></Grid>
                    )}
                    {(ctrl.numZones > 0 || ctrl.stationCount > 0) && (
                      <Grid item xs={6}><Typography variant="body2">Zones/Stations: <strong>{ctrl.numZones || ctrl.stationCount}</strong></Typography></Grid>
                    )}
                    {ctrl.hasWifi !== undefined && (
                      <Grid item xs={6}><Typography variant="body2">WiFi: <strong>{ctrl.hasWifi ? 'Yes' : 'No'}</strong></Typography></Grid>
                    )}
                    {ctrl.hasSensor !== undefined && (
                      <Grid item xs={6}><Typography variant="body2">Rain Sensor: <strong>{ctrl.hasSensor ? `Yes (${ctrl.sensorType || ''})` : 'No'}</strong></Typography></Grid>
                    )}
                    {ctrl.programCount > 0 && (
                      <Grid item xs={6}><Typography variant="body2">Programs: <strong>{ctrl.programCount}</strong></Typography></Grid>
                    )}
                    {ctrl.batteryType && (
                      <Grid item xs={6}><Typography variant="body2">Battery: <strong>{ctrl.batteryType}</strong></Typography></Grid>
                    )}
                  </Grid>
                  {ctrl.notes && (
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>{ctrl.notes}</Typography>
                  )}
                  {/* Controller issues */}
                  {ctrl.issues?.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="subtitle2" gutterBottom>Issues</Typography>
                      {ctrl.issues.map((issue, iIdx) => (
                        <Box key={issue.id || iIdx} className={classes.issueItem} mb={0.5}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>{issue.title || issue.description || 'Issue'}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {(ctrlPhotos.length > 0 || allCtrlPhotos.length > 0) && (
                    <Box mt={1}>
                      <Typography variant="subtitle2" gutterBottom>Photos ({allCtrlPhotos.length + ctrlPhotos.length})</Typography>
                      <div className={classes.photoGrid}>
                        {allCtrlPhotos.map((photoUrl, pIdx) => (
                          <img key={`mp-${pIdx}`} src={photoUrl} alt="Controller photo" className={classes.photoThumb}
                            onClick={() => setLightboxPhoto({ url: photoUrl, caption: `${ctrl.brand || ''} ${ctrl.model || 'Controller'}` })}
                          />
                        ))}
                        {ctrlPhotos.map((photo, pIdx) => (
                          <img key={`ip-${pIdx}`} src={photo.photoUrl} alt="Controller photo" className={classes.photoThumb}
                            onClick={() => setLightboxPhoto({ url: photo.photoUrl, caption: `${ctrl.brand || ''} ${ctrl.model || 'Controller'}` })}
                          />
                        ))}
                      </div>
                    </Box>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </Grid>
        )}

        {/* Valve Boxes */}
        {valveBoxes.length > 0 && (
          <Grid item xs={12} md={controllers.length > 0 ? 6 : 12}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Valve Boxes ({valveBoxes.length})
            </Typography>
            {valveBoxes.map((vb, vIdx) => {
              const vbPhotos = photosByEntityId[vb.id] || photosByEntityId[vb._id?.toString()] || [];
              const modelPhotos = vb.photos || [];
              // Count valves that belong to this valve box
              const vbValves = valves.filter(v => v.valveBoxId && (v.valveBoxId.toString() === (vb._id || '').toString()));
              const notesStr = typeof vb.notes === 'string' ? vb.notes : (Array.isArray(vb.notes) ? vb.notes.map(n => typeof n === 'string' ? n : n.text || '').join(', ') : '');
              const showNotes = notesStr && notesStr !== '[]' && notesStr !== '';
              return (
              <Card key={vb._id || vb.id || vIdx} className={classes.controllerCard} variant="outlined">
                <CardContent style={{ paddingBottom: 12 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      {vb.name || `Valve Box ${vIdx + 1}`}
                    </Typography>
                    <Box>
                      {vb.condition && (
                        <Chip
                          label={vb.condition}
                          size="small"
                          style={{
                            textTransform: 'capitalize',
                            marginRight: 4,
                            backgroundColor: vb.condition === 'good' || vb.condition === 'excellent' ? '#e8f5e9' : vb.condition === 'poor' ? '#ffebee' : '#fff3e0'
                          }}
                        />
                      )}
                      {vb.maintenanceStatus && vb.maintenanceStatus !== 'operational' && (
                        <Chip
                          label={vb.maintenanceStatus.replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                          style={{ textTransform: 'capitalize' }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Grid container spacing={1}>
                    {vbValves.length > 0 && (
                      <Grid item xs={6}><Typography variant="body2">Valves: <strong>{vbValves.length}</strong></Typography></Grid>
                    )}
                    {vb.accessibility?.isBuried && (
                      <Grid item xs={6}><Typography variant="body2">Buried: <strong>Yes</strong>{vb.accessibility.requiresDigging ? ' (needs digging)' : ''}</Typography></Grid>
                    )}
                    {vb.accessibility?.accessRestricted && (
                      <Grid item xs={6}><Typography variant="body2">Access: <strong>Restricted</strong></Typography></Grid>
                    )}
                    {vb.accessibility?.keyRequired && (
                      <Grid item xs={6}><Typography variant="body2">Key Required: <strong>Yes</strong>{vb.accessibility.keyLocation ? ` (${vb.accessibility.keyLocation})` : ''}</Typography></Grid>
                    )}
                  </Grid>
                  {showNotes && (
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>{notesStr}</Typography>
                  )}
                  {/* Valve box issues */}
                  {vb.issues?.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="subtitle2" gutterBottom>Issues</Typography>
                      {vb.issues.map((issue, iIdx) => (
                        <Box key={issue.id || iIdx} className={classes.issueItem} mb={0.5}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {issue.title || issue.description || 'Issue'}
                            {issue.severity && (
                              <Chip label={issue.severity} size="small" style={{ marginLeft: 6, height: 18, fontSize: '0.65rem', backgroundColor: SEVERITY_COLORS[issue.severity] || '#999', color: '#fff' }} />
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {/* Exterior / Interior + all photos */}
                  {(vb.exteriorPhoto || vb.interiorPhoto || vbPhotos.length > 0 || modelPhotos.length > 0) && (
                    <Box mt={1}>
                      <Typography variant="subtitle2" gutterBottom>Photos</Typography>
                      <div className={classes.photoGrid}>
                        {vb.exteriorPhoto && !modelPhotos.includes(vb.exteriorPhoto) && (
                          <Box>
                            <img src={vb.exteriorPhoto} alt="Exterior" className={classes.photoThumb}
                              onClick={() => setLightboxPhoto({ url: vb.exteriorPhoto, caption: `${vb.name || `Valve Box ${vIdx + 1}`} — Exterior` })}
                            />
                            <Chip label="Exterior" size="small" className={classes.photoTag} variant="outlined" />
                          </Box>
                        )}
                        {vb.interiorPhoto && !modelPhotos.includes(vb.interiorPhoto) && (
                          <Box>
                            <img src={vb.interiorPhoto} alt="Interior" className={classes.photoThumb}
                              onClick={() => setLightboxPhoto({ url: vb.interiorPhoto, caption: `${vb.name || `Valve Box ${vIdx + 1}`} — Interior` })}
                            />
                            <Chip label="Interior" size="small" className={classes.photoTag} variant="outlined" />
                          </Box>
                        )}
                        {modelPhotos.map((photoUrl, pIdx) => (
                          <img key={`mp-${pIdx}`} src={photoUrl} alt="Valve box photo" className={classes.photoThumb}
                            onClick={() => setLightboxPhoto({ url: photoUrl, caption: vb.name || `Valve Box ${vIdx + 1}` })}
                          />
                        ))}
                        {vbPhotos.map((photo, pIdx) => (
                          <img key={`ip-${pIdx}`} src={photo.photoUrl} alt="Valve box photo" className={classes.photoThumb}
                            onClick={() => setLightboxPhoto({ url: photo.photoUrl, caption: vb.name || `Valve Box ${vIdx + 1}` })}
                          />
                        ))}
                      </div>
                    </Box>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </Grid>
        )}

        {/* Findings & Recommendations */}
        {(repairsNeeded.length > 0 || recommendations.length > 0) && (
          <Grid item xs={12} md={controllers.length > 0 || valveBoxes.length > 0 ? 6 : 12}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Findings & Recommendations
            </Typography>
            {repairsNeeded.length > 0 && (
              <Paper style={{ padding: 12, marginBottom: 12 }} elevation={1}>
                <Typography variant="subtitle2" gutterBottom>
                  Repairs Needed ({repairsNeeded.length})
                </Typography>
                {repairsNeeded.map((repair, rIdx) => (
                  <Box
                    key={rIdx}
                    className={classes.repairItem}
                    style={{ borderLeft: `3px solid ${PRIORITY_COLORS[repair.priority] || '#999'}` }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {repair.description}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        {repair.priority && (
                          <Chip
                            label={repair.priority}
                            size="small"
                            style={{
                              textTransform: 'capitalize',
                              backgroundColor: PRIORITY_COLORS[repair.priority] || '#999',
                              color: '#fff',
                              height: 20,
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                        {repair.estimatedCost > 0 && (
                          <Typography variant="caption" color="textSecondary">
                            ~${repair.estimatedCost}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Paper>
            )}
            {recommendations.length > 0 && (
              <Paper style={{ padding: 12 }} elevation={1}>
                <Typography variant="subtitle2" gutterBottom>
                  Recommendations
                </Typography>
                <List dense disablePadding>
                  {recommendations.map((rec, rIdx) => (
                    <ListItem key={rIdx} dense disableGutters>
                      <ListItemIcon style={{ minWidth: 28 }}>
                        <CheckCircleIcon style={{ fontSize: 16, color: theme.palette.info.main }} />
                      </ListItemIcon>
                      <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Grid>
        )}

        {/* Photo Gallery */}
        {photos.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Photo Gallery ({photos.length})
            </Typography>
            {Object.keys(photosByType).map((type) => (
              <Box key={type} mb={2}>
                <Typography variant="subtitle2" gutterBottom style={{ textTransform: 'capitalize' }}>
                  {ENTITY_TYPE_LABELS[type] || type} ({photosByType[type].length})
                </Typography>
                <div className={classes.photoGrid}>
                  {photosByType[type].map((photo, pIdx) => (
                    <Box key={photo._id || pIdx}>
                      <img
                        src={photo.photoUrl}
                        alt={`${type} photo`}
                        className={classes.photoThumb}
                        onClick={() => setLightboxPhoto({
                          url: photo.photoUrl,
                          caption: `${ENTITY_TYPE_LABELS[photo.entityType] || photo.entityType}${photo.capturedAt ? ` - ${moment(photo.capturedAt).format('MMM D, h:mm A')}` : ''}`
                        })}
                      />
                      <Chip
                        label={ENTITY_TYPE_LABELS[photo.entityType] || photo.entityType}
                        size="small"
                        className={classes.photoTag}
                        variant="outlined"
                      />
                    </Box>
                  ))}
                </div>
              </Box>
            ))}
          </Grid>
        )}

        {/* Site Review Issues (if any) */}
        {data.siteReviewIssues?.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Site Review Issues ({data.siteReviewIssues.length})
            </Typography>
            <Paper style={{ padding: 12 }} elevation={1}>
              <List dense disablePadding>
                {data.siteReviewIssues.map((issue, iIdx) => (
                  <ListItem key={issue._id || iIdx} dense divider={iIdx < data.siteReviewIssues.length - 1}>
                    <ListItemIcon style={{ minWidth: 32 }}>
                      <WarningIcon style={{ fontSize: 18, color: theme.palette.warning.main }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={issue.description || issue.title || 'Issue'}
                      secondary={issue.submitted_by ? `By ${issue.submitted_by.firstName || ''} ${issue.submitted_by.lastName || ''}` : null}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Lightbox Dialog */}
      <Dialog
        open={!!lightboxPhoto}
        onClose={() => setLightboxPhoto(null)}
        maxWidth="lg"
      >
        <DialogContent style={{ padding: 8, position: 'relative', backgroundColor: '#000' }}>
          <IconButton
            onClick={() => setLightboxPhoto(null)}
            style={{ position: 'absolute', top: 4, right: 4, color: '#fff', zIndex: 1 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          {lightboxPhoto && (
            <>
              <img
                src={lightboxPhoto.url}
                alt="Full size"
                className={classes.lightboxImg}
              />
              {lightboxPhoto.caption && (
                <Typography variant="body2" style={{ color: '#fff', textAlign: 'center', padding: '8px 0' }}>
                  {lightboxPhoto.caption}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const mapStateToProps = (state) => ({
  url: state.url
});

export default connect(mapStateToProps)(InspectionDetailPage);
